import { GetStaticPaths, GetStaticProps } from "next";
import { toHTML } from "@portabletext/to-html";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Breadcrumbs,
  Conversion,
  ConversionCopy as ConversionCopyProps,
  useSlideOverDialogContext,
} from "@bluebottlecoffee/design-system/components";
import {
  IntervalUnit,
  ProductVariant,
  useAnalytics,
  useAuth,
  useCart,
  useProduct,
  useUser,
} from "@chordcommerce/react-autonomy";
import { Result } from "@bluebottlecoffee/design-system/dist/lib/types";
import { Currency } from "dinero.js";
import { trackEvent } from "../../../../lib/segment/track-event";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { getTokenWithRefresh } from "../../../../lib/auth/get-token-with-refresh";
import { toBundle } from "../../../../lib/hooks/useBundle";
import { toQuantityGuideProps } from "../../../../lib/transformers/quantity-guide";
import useVariantPrice from "../../../../lib/chord/hooks/components/use-variant-price";
import {
  toProductRecsCopyProps,
  toProductRecsProps,
} from "../../../../lib/transformers/cart";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "../../../../lib/sanity/translation-group-queries";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import {
  Product as ProductSchema,
  QuantityGuide,
  ShippingCopy as ShippingCopySchema,
} from "../../../../lib/sanity-schema";
import {
  ConversionSchema,
  PrepaidGift,
  toConversionProps,
  toPrepayProps,
} from "../../../../lib/transformers/conversion";
import useVariantAvailability from "../../../../lib/chord/hooks/components/use-variant-availability";
import { CartBasePropsWithSlug } from "../../../../lib/sanity/shared";
import { filters, getSlug } from "../../../../lib/sanity/utils";
import {
  cartVariants,
  getDefaultStaticPropsMiniCartWithSlug,
  hasOptionValues,
} from "../../../../lib/utils";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import {
  YotpoReviews,
  YotpoReviewsLink,
} from "../../../../components/YotpoReviews";
import {
  buildSanityQuery,
  prepayQuery,
  productFilters,
  quantityGuideQuery,
  shippingCopyQuery,
} from "../../../../lib/sanity/product-queries";
import emailIsValid from "../../../../lib/utils/strings/email-is-valid";
import localization from "../../../../localization";
import { REGION } from "../../../../lib/i18n";
import { isFeatureEnabled } from "../../../../lib/utils/is-feature-enabled";
import { toTrackProductProps } from "../../../../lib/chord/transformers/product";
import { useIsClient } from "../../../../lib/hooks/use-is-client";
import { productPage } from "../../../../lib/link-builders";
import { prepaidSubOptions } from "../../../../lib/chord/services/oms/prepaid-sub-options";
import { experiments, useExperiment } from "../../../../lib/ABTesting";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";

export type ConversionCopy = ConversionCopyProps & {
  adding: string;
  readMoreTriggerAriaLabel: string;
  readMoreTriggerLabel: string;
};

interface PageData extends CartBasePropsWithSlug {
  conversion: ConversionSchema;
  shippingCopy: ShippingCopySchema;
  copy: ConversionCopy;
  quantityGuide: QuantityGuide;
  prepayGift: PrepaidGift;
}

type QueryType = {
  conversion: ConversionSchema;
  shippingCopy: ShippingCopySchema;
  quantityGuide: QuantityGuide;
  prepayGift: PrepaidGift;
};

const addMetaTagForNoFollowProduct = (
  conversion: ConversionSchema,
  lang: string,
): void => {
  if (conversion.excludeFromIndex) {
    if (!Array.isArray(conversion.metaTags)) {
      // eslint-disable-next-line no-param-reassign
      conversion.metaTags = [];
    }

    conversion.metaTags.push({
      _type: "metaTag",
      _key: "",
      name: "robots",
      content: {
        _type: "localizedString",
        [lang]: "noindex,nofollow",
      },
    });
  }
};

function isValidConversion(p: ConversionSchema, lang: string): boolean {
  const test: Function = (field: string): boolean => {
    if (!p[field]) {
      if (p.name && p.name[lang]) {
        console.warn(`${p.name[lang]} does not have ${field}, skipping...`);
        return false;
      } else {
        throw Error(
          `Product with sanity id [${p._id}] is published and does not have a name in ${lang}.`,
        );
      }
    } else if (Array.isArray(p[field])) {
      if (p[field].length === 0) {
        if (p.name && p.name[lang]) {
          console.warn(`${p.name[lang]} has an empty ${field}, skipping...`);
          return false;
        } else {
          throw Error(
            `Product with sanity id [${p._id}] is published and does not have a name in ${lang}.`,
          );
        }
      }
    }

    return true;
  };

  // ensure require fields exist
  return !(
    !test("slug") ||
    !test("descriptionV2") ||
    !test("images") ||
    !test("variants") ||
    !hasOptionValues(p, lang)
  );
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } =
    await getDefaultStaticPropsMiniCartWithSlug(context);

  const { lang, region, slug } = defaultProps;

  // A Combined query for the product specific conversion module
  // and the shipping copy Document in Sanity
  const query = `{
    'conversion': ${buildSanityQuery(lang, slug)},
    'prepayGift': ${prepayQuery(lang)},
    'shippingCopy': ${shippingCopyQuery},
    'quantityGuide': ${quantityGuideQuery},
  }`;

  const translationGroupQuery = buildTranslationGroupQuery(lang, "Conversion");

  const copy = await sanityClient()
    .fetch<TranslationGroupSanityType>(translationGroupQuery)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<ConversionCopy>(lang, translations),
    );

  const { conversion, shippingCopy, quantityGuide, prepayGift } =
    await sanityClient().fetch<QueryType>(query);

  if (!isValidConversion(conversion, lang)) return { notFound: true };

  await resolveTabbedCollections(conversion.modules, { lang, region });

  return {
    props: {
      ...defaultProps,
      conversion,
      shippingCopy,
      copy,
      quantityGuide,
      prepayGift,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const productSlugs = await sanityClient().fetch<
    ProductSchema["slug"]["current"][]
  >(`*[
    ${filters.whereType("product")}
    && ${productFilters}
  ].${getSlug<ProductSchema>("slug")}`);

  const params = localization.languages(REGION).flatMap((lang) =>
    productSlugs.map((slug) => ({
      params: {
        region: REGION,
        lang: lang.code,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

function validate(data: ConversionSchema, lang: string) {
  if (!data.slug) {
    throw Error("Page must have a slug.");
  }
  const cSProductNotForSale =
    data.showCrossSell &&
    data.crossSellProducts.length &&
    data.crossSellProducts.some((product) => !product.forSale);
  if (cSProductNotForSale) {
    throw Error(
      `A cross-sell product on ${data.name[lang]} has been marked not for sale.`,
    );
  }
}

const Page: FunctionComponent<PageData> = ({
  conversion,
  shippingCopy,
  copy,
  quantityGuide,
  prepayGift,
  ...miniCartLayoutWrapperProps
}) => {
  validate(conversion, miniCartLayoutWrapperProps.lang);
  const { trackProductViewed } = useAnalytics();

  const [isWaitListed, setIsWaitListed] = useState<boolean>(false);
  const [waitListError, setWaitListError] = useState<boolean>(false);
  const isClient = useIsClient();
  /**
   * giftCardFormCopy is only pulled once but used in multiple places
   * 1. MiniCartDialog
   * 2. GiftCardForm (in Conversion)
   */
  const {
    cart: sanityCart,
    region,
    lang,
    giftCardFormCopy,
    navAndCartInfoBanner,
    subscribableProducts,
    aria,
  } = miniCartLayoutWrapperProps;
  const { adding } = copy;
  const {
    abTestBenefits,
    benefits,
    staticShippingCopy,
    subscriptionBenefitsCopy,
    subscriptionPriceDisclaimerCopy,
    subscriptionFreeShippingCopy,
  } = shippingCopy;
  const isSubscriptionBenefitsVariant =
    useExperiment<string>(experiments.pdpSubscriptionBenefits, "control") ===
    "variant";
  const { cart, addToCart, addSubscription, modifyGiftCards } = useCart();
  const { createStockRequest } = useProduct();
  const { user, loadUser } = useUser();

  addMetaTagForNoFollowProduct(conversion, lang);

  /**
   * The AB test variant takes precedence over per product benefits so every
   * bucketed PDP communicates the same value proposition.
   * Otherwise: if useCustomShippingBenefits, use customShippingBenefits,
   * else use defaults from shippingCopy
   */
  const getShippingBenefits = () => {
    if (isSubscriptionBenefitsVariant && abTestBenefits?.length) {
      return abTestBenefits.map((benefit) => ({
        isSubscriberBenefit: benefit.isSubscriberBenefit,
        text: benefit.text[lang],
        fallbackText: benefit.fallbackText?.[lang],
      }));
    }

    if (
      conversion.useCustomShippingBenefits &&
      conversion.customShippingBenefits?.length
    ) {
      return conversion.customShippingBenefits.map((benefit) => ({
        isSubscriberBenefit: benefit.isSubscriberOnlyBenefit,
        text: benefit.text[lang],
      }));
    }

    return benefits.map((benefit) => ({
      ...benefit,
      text: benefit.text[lang],
    }));
  };

  const shippingBenefits = getShippingBenefits();

  const miniCartDialog = (
    <MiniCartDialog
      giftCardFormCopy={giftCardFormCopy}
      miniCartCopy={miniCartLayoutWrapperProps.cart}
      navAndCartInfoBanner={navAndCartInfoBanner}
      productRecs={miniCartLayoutWrapperProps.productRecs}
      region={region}
      lang={lang}
      subscribableProducts={
        isFeatureEnabled(
          process.env.NEXT_PUBLIC_CART_SUBSCRIPTION_TOGGLE_ENABLED,
        )
          ? subscribableProducts
          : []
      }
    />
  );
  const lineItems = cartVariants({
    data: cart,
    dialect: { region, lang },
    oneTimeLabel: miniCartLayoutWrapperProps.cart.oneTimePurchase[lang],
  });

  async function handleCreateStockRequest({
    email,
    sku,
  }: {
    email: string;
    sku: string;
  }): Promise<void> {
    try {
      let { product } = toTrackProductProps(conversion, lang);
      if (product?.variant?.sku !== sku) {
        product = { variant: { sku } as ProductVariant };
      }
      await createStockRequest({
        email,
        product,
      });
      setIsWaitListed(true);
    } catch (error) {
      setWaitListError(true);
      console.error(error);
    }
  }

  // Add To Cart & Subscribe //
  const { setActiveContentType: setDialogContentType } =
    useSlideOverDialogContext();
  const [atcLoading, setAtcLoading] = useState<boolean>(false);
  const [addToCartErrorMessage, setAddToCartErrorMessage] =
    useState<string>(null);
  const handleAddToCart = async (sku: string, quantity: number) => {
    try {
      setAddToCartErrorMessage(null);
      setAtcLoading(true);
      await addToCart({
        sku,
        quantity,
      });
      setDialogContentType("minicart");
      setAtcLoading(false);
    } catch (error) {
      setDialogContentType(null);
      setAddToCartErrorMessage(error.message);
      setAtcLoading(false);
    }
  };
  const handleSubscribe = async (options: {
    interval: { length: number; unit: IntervalUnit };
    quantity: number;
    sku: string;
  }) => {
    try {
      setAtcLoading(true);
      setAddToCartErrorMessage(null);
      await addSubscription({
        endDate: null,
        interval: {
          length: options.interval.length,
          unit: options.interval.unit,
        },
        quantity: options.quantity,
        sku: options.sku,
      });
      setDialogContentType("minicart");
      setAtcLoading(false);
    } catch (error) {
      setDialogContentType(null);
      setAddToCartErrorMessage(error.message);
      setAtcLoading(false);
    }
  };

  const handleAddPrepaidSubscription = async (options: {
    interval: { length: number; unit: IntervalUnit };
    quantity: number;
    sku: string;
  }) => {
    try {
      const { sku, quantity, interval } = options;
      setAddToCartErrorMessage(null);
      setAtcLoading(true);
      // we have to use addToCart here instead of addSubscription because
      // addSubscription does not support an options object for prepaid subscriptions
      // we also cannot use purchasePrepaidSubscription because it creates an order
      // meant to be plugged into Stripe, bypassing the cart.
      await addToCart({
        sku,
        quantity,
        // @ts-ignore Chord type mismatch
        options: prepaidSubOptions({
          sku,
          interval,
          installmentCount: prepayGift.numberOfInstallments,
        }),
      });
      setDialogContentType("minicart");
      setAtcLoading(false);
    } catch (error) {
      setDialogContentType(null);
      setAddToCartErrorMessage(error.message);
      setAtcLoading(false);
    }
  };

  const handleAddToCardProductRecs = async (
    sku: string,
    quantity: number,
  ): Promise<Result> => {
    try {
      const data = await addToCart({ sku, quantity });

      trackEvent("pdp_you_may_also_like_add", {
        name: data.lineItems[0].variant.name,
        quantity,
        sku,
      });

      setDialogContentType("minicart");
      return {
        success: true,
        message: "Success.",
      };
    } catch (e) {
      return {
        success: false,
        message: e?.message ?? "Sorry, something went wrong.",
      };
    }
  };

  const { getToken } = useAuth();

  useEffect(() => {
    try {
      getTokenWithRefresh(getToken);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (!user.data.email) {
      try {
        loadUser();
      } catch (error) {
        console.error(error);
      }
    }
  }, [user.data]);

  useEffect(() => {
    try {
      trackProductViewed(toTrackProductProps(conversion, lang));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const conversionProductProps = useMemo(
    () => toConversionProps(conversion, lang).product,
    [conversion, lang],
  );

  const prepayProps = useMemo(
    () => toPrepayProps(prepayGift, lang),
    [prepayGift, lang],
  );

  const crossSellProps = useMemo(
    () =>
      conversion.crossSellProducts && {
        addToCartClick: handleAddToCardProductRecs,
        emptyCart: false,
        productRecs: toProductRecsProps(
          conversion.crossSellProducts,
          region,
          lang,
          cart.currency as Currency,
        ),
        productRecShopCards: [],
        ...toProductRecsCopyProps(
          sanityCart,
          lang,
          conversion.crossSellHeading?.[lang],
        ),
      },
    [conversion.crossSellProducts, region, lang, cart.currency, sanityCart],
  );

  const shippingCopyProps = useMemo(
    () => ({
      benefits: shippingBenefits,
      staticShippingCopy: toHTML(staticShippingCopy[lang]),
      subscriptionBenefitsCopy: toHTML(subscriptionBenefitsCopy?.[lang]),
      subscriptionPriceDisclaimerCopy: subscriptionPriceDisclaimerCopy?.[lang],
      subscriptionFreeShippingCopy: subscriptionFreeShippingCopy?.[lang],
    }),
    [shippingBenefits, shippingCopy, lang],
  );

  const modifyGiftCardsFn = useCallback(async (options) => {
    await modifyGiftCards({
      giftCards: {
        giftCardsDetails: options.giftCardsDetails.map(({ id, ...rest }) => ({
          id: id.toString(),
          ...rest,
        })),
      },
    });
  }, []);

  const showQuantityGuideProp = useMemo(
    () =>
      conversion.optionTypes[0]?.name.includes("Bag Size") &&
      conversion.variants.length !== 1,
    [conversion],
  );

  const useBundleProp = useMemo(
    () =>
      toBundle({
        conversion,
        setDialogContentType,
        lang,
        copy: {
          adding,
        },
      }),
    [conversion, lang, adding],
  );

  return (
    isClient && (
      <>
        <CanonicalHead
          region={region}
          lang={lang}
          slug={`product/${conversion.slug.current}`}
        />
        <LayoutWrapper
          key={conversion.name[lang]} // reset page when linked to a PDP from a PDP
          {...{
            ...miniCartLayoutWrapperProps,
            altBBLogo: conversion.altBBLogo,
            pageTitle: `${conversion.name[lang]} | Blue Bottle Coffee`,
            metaTags: conversion.metaTags,
            slideOutContent: [miniCartDialog],
            openMiniCartPanel: Boolean(miniCartDialog),
          }}
        >
          <Breadcrumbs
            backToResultsLabel={copy.backToResults}
            onBackToResult={({ setActiveContentType }) =>
              setActiveContentType("search")
            }
            bottomSpace
          />

          <Conversion
            product={conversionProductProps}
            showCrossSell={conversion.showCrossSell}
            crossSell={crossSellProps}
            copy={copy}
            shippingCopy={shippingCopyProps}
            addToCart={handleAddToCart}
            addToCartProps={{
              loadingMessage: adding,
              loading: atcLoading,
              errorMessage: addToCartErrorMessage,
            }}
            modifyGiftCards={modifyGiftCardsFn}
            lineItems={lineItems}
            createStockRequest={handleCreateStockRequest}
            giftCardFormCopy={giftCardFormCopy}
            subscribeProduct={handleSubscribe}
            useVariantAvailability={useVariantAvailability}
            useVariantPrice={useVariantPrice}
            isWaitListed={isWaitListed}
            waitListError={waitListError}
            waitListEmailDefault={user?.data.email}
            emailIsValid={emailIsValid}
            showQuantityGuide={showQuantityGuideProp}
            quantityGuide={toQuantityGuideProps(quantityGuide, lang)}
            useBundle={useBundleProp}
            reviews={
              <YotpoReviewsLink productId={conversion.variants[0].sku} />
            }
            clickTracking={trackEvent}
            addPrepaySubscription={handleAddPrepaidSubscription}
            prepayProps={prepayProps}
          />

          {conversion.modules.map((module) => {
            try {
              return renderModule({
                module,
                region,
                lang,
                aria,
              });
            } catch (error) {
              // eslint-disable-next-line no-console
              console.warn(
                `Skipping product module "${module?._type}":`,
                error,
              );
              return null;
            }
          })}

          <YotpoReviews
            productId={conversion.variants[0].sku}
            productName={conversion.name[lang]}
            productUrl={productPage({
              slug: conversion.slug.current,
              lang,
              region,
            })}
            productImageUrl={conversion.images[0].desktop.source.url}
          />
        </LayoutWrapper>
      </>
    )
  );
};

export default Page;
